import ErrorList from "../components/ErrorList"
import ProjectsDashboard from "../projects/components/ProjectsDashboard"

import { Box, CircularProgress, Grid, makeStyles, Theme, Typography } from "@material-ui/core"
import { getProjectQueryFromContext } from "../context-options/utils"
import { useAppContext } from "../app-context/use-app-context"
import { ProjectsDashboardQueryParams, useGetProjectsDashboardQuery } from "../projects/api"
import { AppMainContextType, AppSubContextType } from "../types/app-context"
import { useI18n, useRouter } from "../hooks"
import { APP_MAX_WIDTH } from "../constants"
import { useEffect, useState } from "react"
import { useIsSmallScreen } from "../hooks/use-is-small-screen"

const queryOptions = { refetchOnMountOrArgChange: true } as const

const ProjectsDashboardRoute = () => {
  const isSmallScreen = useIsSmallScreen("xs")
  const classes = useStyles()
  const translations = useTranslations()
  const { query: routerQuery } = useRouter()
  const { appContext } = useAppContext()
  const { mainContext, subContext } = appContext
  const context = subContext || mainContext

  const [query, setQuery] = useState<ProjectsDashboardQueryParams>(makeProjectsDashboardQuery(context))
  const { data: dashboard, isLoading, isError } = useGetProjectsDashboardQuery(query, queryOptions)

  useEffect(() => {
    if (routerQuery) {
      const newQuery = makeProjectsDashboardQuery(context)
      if (JSON.stringify(query) !== JSON.stringify(newQuery)) {
        setQuery(newQuery)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerQuery])

  if (isLoading) return <CircularProgress />
  if (isError) return <ErrorList errors={[translations.fetchErrorProjectsDashboard]} />
  if (!context) return <ErrorList errors={[translations.contextNotFoundError]} />
  if (!dashboard) return <ErrorList errors={[translations.fetchErrorProjectsDashboard]} />

  return (
    <Grid container alignItems="center" direction="column">
      <Grid item container alignItems="center" direction="column" spacing={2} className={classes.container}>
        <Grid item className={classes.headerContainer}>
          <Typography variant="h4">
            {!isSmallScreen && (
              <Box component="span" color="text.secondary">
                {`${context.name} - `}
              </Box>
            )}
            <Box component="span"> {translations.pageTitleLabel} </Box>
          </Typography>
        </Grid>
        <Grid item className={classes.bodyContainer}>
          <ProjectsDashboard
            dashboard={dashboard}
            handleUpdateQuery={(query) => {
              setQuery(query)
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(0, 1),
    maxWidth: APP_MAX_WIDTH,
  },
  headerContainer: {
    display: "flex",
    margin: theme.spacing(0.75, 0, 1.125, 0),
    "& > :first-child": {
      flexGrow: 1,
      marginRight: 32,
    },
    width: "100%",
  },
  bodyContainer: {
    width: "100%",
  },
}))

const useTranslations = (defaults: Translations = defaultTranslations): Translations => {
  const { translations = {} } = useI18n("translation")

  const pageTitleLabel = translations.appNavBar?.projectsDashboardLabel || defaults.pageTitleLabel
  const {
    fetchErrorProjectsDashboard = defaults.fetchErrorProjectsDashboard,
    contextNotFoundError = defaults.contextNotFoundError,
    projectsDashboardNotFoundError = defaults.projectsDashboardNotFoundError,
  } = translations

  return {
    pageTitleLabel,
    contextNotFoundError,
    fetchErrorProjectsDashboard,
    projectsDashboardNotFoundError,
  }
}
const defaultTranslations = {
  pageTitleLabel: "Dashboard",
  contextNotFoundError: "Context not found",
  fetchErrorProjectsDashboard: "Failed to fetch projects-dashboard",
  projectsDashboardNotFoundError: "Projects-dashboard not found",
}

const makeProjectsDashboardQuery = (
  context: AppMainContextType | AppSubContextType | null
): ProjectsDashboardQueryParams => {
  return {
    archived: "false",
    ...(getProjectQueryFromContext(context) as ProjectsDashboardQueryParams),
  }
}

export default ProjectsDashboardRoute
type Translations = typeof defaultTranslations
